import type { ProductInformation } from '@dealerportal/common';
import trimEnd from 'es-toolkit/compat/trimEnd';

/**
 * Where 3D models are served from when `NEXT_PUBLIC_KBF_MODEL_BASE_URL` is
 * unset: the app's `public/models` folder, so a `.glb` exported from the CiC
 * Test Hub can be dropped in as `apps/dealerportal/public/models/<code>.glb`.
 */
export const DEFAULT_MODEL_BASE_URL = '/models';

/**
 * The manufacturer item code behind a product, e.g. `92784.b12l` → `b12l`.
 * Falls back to the slug, which for CiC items is the same code.
 */
export const itemCodeOf = (
  productInfo: Pick<ProductInformation, 'productId' | 'slug'>
): string => {
  const [, ...rest] = (productInfo.productId ?? '').split('.');
  return rest.join('.') || productInfo.slug || '';
};

/**
 * URL of a static `.glb` for a product, looked up by item code under a base
 * URL. This is the *fallback* model: the live one for the current
 * configuration comes from the CiC SDK (see `useItemModel`), which is only
 * available on dealer sites that load the catalog script. Storybook, tests
 * and a failed geometry request land here instead.
 */
export const resolveModelSrc = (
  productInfo: Pick<ProductInformation, 'productId' | 'slug'>,
  baseUrl: string = process.env.NEXT_PUBLIC_KBF_MODEL_BASE_URL ||
    DEFAULT_MODEL_BASE_URL
): string | undefined => {
  const code = itemCodeOf(productInfo);
  if (!code) return undefined;
  return `${trimEnd(baseUrl, '/')}/${encodeURIComponent(code.toLowerCase())}.glb`;
};
